import React, { useEffect, useRef, useState } from "react";
import AdminLayout from "../../../components/layouts/adminLayout";
import AdminPageContainer from "../../../components/AdminPageContainer";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { Form } from "react-bootstrap";
import LicenseMasterServices from "../../../services/API/LicenseMaster";
import Select from "react-select";

const toOption = (item) => ({ value: item.id, label: item.name });

export default function DocumentChecklist() {
  const navigate = useNavigate();
  const dataFetchedRef = useRef(false);

  // Dropdown data lists
  const [licenses, setLicenses] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [companyTypeList, setCompanyTypeList] = useState([]);
  const [industryTypeList, setIndustryTypeList] = useState([]);
  const [statesList, setStatesList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [municipalList, setMunicipalList] = useState([]);

  // Selected values
  const [license, setLicense] = useState(null);
  const [licenseType, setLicenseType] = useState(null);
  const [companyType, setCompanyType] = useState(null);
  const [industryType, setIndustryType] = useState(null);
  const [state, setState] = useState(null);
  const [city, setCity] = useState(null);
  const [municipal, setMunicipal] = useState(null);

  // License mapping metadata (is_selected, is_company, is_industry)
  const [licenseMapping, setLicenseMapping] = useState(null);
  const [licenseMappingId, setLicenseMappingId] = useState(null);

  // Loading flags
  const [licenseTypeLoader, setLicenseTypeLoader] = useState(false);
  const [companyLoader, setCompanyLoader] = useState(false);
  const [industryLoader, setIndustryLoader] = useState(false);
  const [statesLoader, setStatesLoader] = useState(false);
  const [citiesLoader, setCitiesLoader] = useState(false);
  const [municipalLoader, setMunicipalLoader] = useState(false);

  // Checklist results
  const [checklist, setChecklist] = useState([]);
  const [fetchingChecklist, setFetchingChecklist] = useState(false);

  const allSelected =
    !!license && !!licenseType && !!companyType &&
    !!industryType && !!state && !!city && !!municipal;

  // Load licenses on mount
  useEffect(() => {
    if (dataFetchedRef.current) return;
    LicenseMasterServices.getAllLicense({ active: 1 }).then((licRes) => {
      if (licRes?.data?.length) {
        setLicenses(
          licRes.data
            .filter((i) => i.status !== 0)
            .sort((a, b) => b.status - a.status)
        );
      }
      dataFetchedRef.current = true;
    });
  }, []);

  // Single function — all dropdown values set purely from getLicenseMapping response.
  // type -1: license selected         → classifications  from data.licenseTypes
  // type 0: license type selected     → companyTypeList  from data.companyTypes
  // type 1: company type selected     → industryTypeList from data.industryTypes + statesList
  // type 2: industry type selected    → statesList from data.states
  // type 3: state selected            → cityList from data.cities
  // type 4: city selected             → municipalList from data.municipals
  const getLicenseMapping = async (payload) => {
    try {
      if (payload.type === -1) setLicenseTypeLoader(true);
      if (payload.type === 0) setCompanyLoader(true);
      if (payload.type === 1) setIndustryLoader(true);
      if (payload.type >= 1 && payload.type <= 2) setStatesLoader(true);
      if (payload.type === 3) setCitiesLoader(true);
      if (payload.type === 4) setMunicipalLoader(true);

      const res = await LicenseMasterServices.getLicenseMapping(payload);
      const data = res;

      if (data?.license_mapping?.length) {
        setLicenseMapping(data.license_mapping[0]);
        setLicenseMappingId(data.license_mapping[0].id);
      }

      if (payload.type === -1) {
        setClassifications(data?.licenseTypes || []);
        setLicenseTypeLoader(false);
      }

      if (payload.type === 0) {
        setCompanyTypeList(data?.companyTypes || []);
        setCompanyLoader(false);
      }

      if (payload.type === 1) {
        setIndustryTypeList(data?.industryTypes || []);
        setIndustryLoader(false);
      }

      if (payload.type >= 1 && payload.type <= 2) {
        setStatesList(data?.states || []);
        setStatesLoader(false);
      }

      if (payload.type === 3) {
        setCityList(data?.cities || []);
        setCitiesLoader(false);
      }

      if (payload.type === 4) {
        setMunicipalList(data?.municipals || []);
        setMunicipalLoader(false);
      }
    } catch {
      toast.error("Failed to load mapping data.");
      setLicenseTypeLoader(false);
      setCompanyLoader(false);
      setIndustryLoader(false);
      setStatesLoader(false);
      setCitiesLoader(false);
      setMunicipalLoader(false);
    }
  };

  const onLicenseTypeChange = (option) => {
    setLicenseType(option);
    setCompanyType(null);
    setIndustryType(null);
    setState(null);
    setCity(null);
    setMunicipal(null);
    setCompanyTypeList([]);
    setIndustryTypeList([]);
    setStatesList([]);
    setCityList([]);
    setMunicipalList([]);
    setChecklist([]);

    if (!option || !license) return;

    getLicenseMapping({
      license: license.value,
      license_type_id: option.value,
      type: 0,
    });
  };

  const checkMapping = (val, type) => {
    setState(null);
    setCity(null);
    setMunicipal(null);
    setStatesList([]);
    setCityList([]);
    setMunicipalList([]);
    setChecklist([]);

    if (!license || !licenseType) return;

    if (type === 1) {
      setIndustryType(null);
      setIndustryTypeList([]);
      getLicenseMapping({
        license: license.value,
        license_type_id: licenseType.value,
        companyType: val,
        type: 1,
      });
    } else {
      getLicenseMapping({
        license: license.value,
        license_type_id: licenseType.value,
        companyType: companyType?.value,
        industryType: val,
        type: 2,
      });
    }
  };

  const getCities = (stateId) => {
    setCity(null);
    setMunicipal(null);
    setCityList([]);
    setMunicipalList([]);
    setChecklist([]);
    getLicenseMapping({
      license: license.value,
      license_type_id: licenseType.value,
      companyType: companyType?.value,
      industryType: industryType?.value,
      state_id: stateId,
      type: 3,
    });
  };

  const getMunicipals = (cityId) => {
    setMunicipal(null);
    setMunicipalList([]);
    setChecklist([]);
    getLicenseMapping({
      license: license.value,
      license_type_id: licenseType.value,
      companyType: companyType?.value,
      industryType: industryType?.value,
      state_id: state?.value,
      city_id: cityId,
      type: 4,
    });
  };

  // Auto-fetch checklist once all 7 dropdowns are filled
  useEffect(() => {
    if (!allSelected) {
      setChecklist([]);
      return;
    }
    const fetchChecklist = async () => {
      setFetchingChecklist(true);
      const resp = await LicenseMasterServices.getDocumentList({
        license: license.value,
        companyType: companyType.value,
        industryType: industryType.value,
        state: state.value,
        city: city.value,
        municipality: municipal.value,
        service_type: licenseType.value,
        license_mapping_id: licenseMappingId,
      });
      setFetchingChecklist(false);
      if (resp) {
        setChecklist(resp?.documents || []);
      } else {
        setChecklist([]);
        toast.error("Unable to get documents");
      }
    };
    fetchChecklist();
  }, [license, licenseType, companyType, industryType, state, city, municipal]);

  const selectStyles = {
    control: (base) => ({ ...base, fontSize: "12px", minHeight: "36px" }),
    option: (base) => ({ ...base, fontSize: "12px" }),
  };

  return (
    <AdminLayout test="active">
      <AdminPageContainer>
        <div id="main" role="main">
          <div id="content">
            <div>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <a
                    style={{ color: "blue", cursor: "pointer" }}
                    onClick={() => navigate("/customer-support/dashboard")}
                    onKeyDown={() => navigate("/customer-support/dashboard")}
                  >
                    Home
                  </a>
                </li>
                <li className="breadcrumb-item active">Document Checklist Preview</li>
              </ol>
            </div>

            <div className="row">
              <div className="col-md-12">
                <div className="card card-sm card-shadow mb-3">
                  <div className="card-body">
                    <h2 style={{ fontWeight: "bold", marginBottom: "16px" }}>
                      Document Checklist Preview
                    </h2>

                    <form className="smart-form">
                      <fieldset>
                        {/* Row 1: License | License Type | Organisation Type */}
                        <div className="row mapping-license">
                          <section className="col col-4">
                            <label className="label">
                              Select License
                              <span style={{ color: "red" }}> *</span>
                            </label>
                            <Select
                              options={licenses.map(toOption)}
                              value={license}
                              onChange={(option) => {
                                setLicense(option);
                                setLicenseType(null);
                                setCompanyType(null);
                                setIndustryType(null);
                                setState(null);
                                setCity(null);
                                setMunicipal(null);
                                setClassifications([]);
                                setCompanyTypeList([]);
                                setIndustryTypeList([]);
                                setStatesList([]);
                                setCityList([]);
                                setMunicipalList([]);
                                setChecklist([]);
                                if (!option) return;
                                getLicenseMapping({ license: option.value, type: -1 });
                              }}
                              placeholder="Select License"
                              isClearable
                              styles={selectStyles}
                            />
                          </section>

                          <section className="col col-4">
                            <label className="label">
                              Select License Type
                              <span style={{ color: "red" }}> *</span>
                            </label>
                            <Select
                              options={classifications.map(toOption)}
                              value={licenseType}
                              onChange={onLicenseTypeChange}
                              placeholder="Select License Type"
                              isClearable
                              isDisabled={!license}
                              isLoading={licenseTypeLoader}
                              styles={selectStyles}
                            />
                          </section>

                          <section className="col col-4">
                            <label className="label">
                              Select Organisation Type
                              <span style={{ color: "red" }}> *</span>
                            </label>
                            <Select
                              options={companyTypeList.map(toOption)}
                              value={companyType}
                              onChange={(option) => {
                                setCompanyType(option);
                                if (option) checkMapping(option.value, 1);
                                else {
                                  setIndustryType(null);
                                  setIndustryTypeList([]);
                                  setState(null);
                                  setStatesList([]);
                                  setCity(null);
                                  setCityList([]);
                                  setMunicipal(null);
                                  setMunicipalList([]);
                                }
                              }}
                              placeholder="Select Organisation Type"
                              isClearable
                              isDisabled={!licenseType}
                              isLoading={companyLoader}
                              styles={selectStyles}
                            />
                          </section>
                        </div>

                        {/* Row 2: Business Type | State | City */}
                        <div className="row mapping-license">
                          <section className="col col-4">
                            <label className="label">
                              Select Business Type
                              <span style={{ color: "red" }}> *</span>
                            </label>
                            <Select
                              options={industryTypeList.map(toOption)}
                              value={industryType}
                              onChange={(option) => {
                                setIndustryType(option);
                                if (option) checkMapping(option.value, 2);
                                else {
                                  setState(null);
                                  setStatesList([]);
                                  setCity(null);
                                  setCityList([]);
                                  setMunicipal(null);
                                  setMunicipalList([]);
                                }
                              }}
                              placeholder="Select Business Type"
                              isClearable
                              isDisabled={!companyType}
                              isLoading={industryLoader}
                              styles={selectStyles}
                            />
                          </section>

                          <section className="col col-4">
                            <label className="label">
                              Select State
                              <span style={{ color: "red" }}> *</span>
                            </label>
                            <Select
                              options={statesList.map(toOption)}
                              value={state}
                              onChange={(option) => {
                                setState(option);
                                if (option) getCities(option.value);
                                else {
                                  setCity(null);
                                  setCityList([]);
                                  setMunicipal(null);
                                  setMunicipalList([]);
                                }
                              }}
                              placeholder="Select State"
                              isClearable
                              isDisabled={!industryType}
                              isLoading={statesLoader}
                              styles={selectStyles}
                            />
                          </section>

                          <section className="col col-4">
                            <label className="label">
                              Select City
                              <span style={{ color: "red" }}> *</span>
                            </label>
                            <Select
                              options={cityList.map(toOption)}
                              value={city}
                              onChange={(option) => {
                                setCity(option);
                                if (option) getMunicipals(option.value);
                                else {
                                  setMunicipal(null);
                                  setMunicipalList([]);
                                }
                              }}
                              placeholder="Select City"
                              isClearable
                              isDisabled={!state}
                              isLoading={citiesLoader}
                              styles={selectStyles}
                            />
                          </section>
                        </div>

                        {/* Row 3: Municipal Corporation */}
                        <div className="row mapping-license">
                          <section className="col col-4">
                            <label className="label">
                              Select Municipal Corporation
                              <span style={{ color: "red" }}> *</span>
                            </label>
                            <Select
                              options={municipalList.map(toOption)}
                              value={municipal}
                              onChange={(option) => {
                                setMunicipal(option);
                                if (!option) setChecklist([]);
                              }}
                              placeholder="Select Municipal Corporation"
                              isClearable
                              isDisabled={!city}
                              isLoading={municipalLoader}
                              styles={selectStyles}
                            />
                          </section>
                        </div>
                      </fieldset>
                    </form>

                    {/* Checklist results */}
                    {allSelected && (
                      <div className="mt-4">
                        <h4 style={{ fontWeight: "600", marginBottom: "16px" }}>
                          Here’s a list of documents needed:
                        </h4>
                        {fetchingChecklist ? (
                          <p className="text-muted">Loading...</p>
                        ) : checklist.length > 0 ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            {checklist.map((item, index) => (
                              <div
                                key={item?.id ?? index}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "14px",
                                  padding: "12px 18px",
                                  backgroundColor: "white",
                                  borderRadius: "8px",
                                  border: "1px solid #ececec",
                                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                                }}
                              >
                                <div style={{ width: "28px", height: "28px", borderRadius: "50%", background: "#edfaf1", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                  <i className="fa fa-check" style={{ color: "#27ae60", fontSize: "12px" }} />
                                </div>
                                <span style={{ fontSize: "13px", color: "#333", fontWeight: "500", flex: 1 }}>
                                  {item?.name || item?.document_name || item}
                                  {item?.is_mandate === 1 && (
                                    <span style={{ color: "red", marginLeft: "4px", fontWeight: "700" }}>*</span>
                                  )}
                                </span>
                                {/* {item?.is_mandate === 1 && (
                                  <span style={{ fontSize: "10px", background: "#fff0f0", color: "#e53935", border: "1px solid #ffd5d5", borderRadius: "4px", padding: "2px 8px", fontWeight: "600", whiteSpace: "nowrap" }}>
                                    Mandatory
                                  </span>
                                )} */}
                              </div>
                            ))}
                            <p style={{ fontSize: "11px", color: "#aaa", marginTop: "8px" }}>
                              <span style={{ color: "red", fontWeight: "700" }}>*</span> Mandatory documents
                            </p>
                          </div>
                        ) : (
                          <p className="text-muted">
                            No documents found for the selected criteria.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AdminPageContainer>
    </AdminLayout>
  );
}
